import type { Locale } from "@/types"

/**
 * OKS-1000 — THE creator-side gift-value formatter (BRIEF D13 / §4.1).
 *
 * # A consolidation of three, not a new function
 *
 * `gift-line.tsx`, `product-block.tsx` and `offer-list.tsx` each carried a
 * byte-similar `formatValue`, and each one's doc-comment said it existed to
 * "mirror" the others so the surfaces "cannot disagree". Three copies agreeing
 * by inspection survived only because nothing ever changed. This ticket adds
 * two decimals and a range prefix to all of them at once, which is precisely
 * the edit that would have drifted.
 *
 * ⚠️ **Takes the two VALUES, not a product-shaped object.** The shipped
 * `offerValue` had to fabricate one to reuse its formatter:
 *
 *     formatValue({ value_cents: max, currency } as unknown as CollabProduct, locale)
 *
 * A double cast through `unknown` disables the check that the argument is a
 * product at all, on the one call whose argument deliberately is not one. This
 * signature removes the need for it — there is nothing to fabricate. A later
 * "simplification" back to taking a product would quietly reintroduce that cast,
 * so the shape here is load-bearing rather than stylistic.
 *
 * # ⚠️ Two fraction digits, changed from the shipped `maximumFractionDigits: 0`
 *
 * Beyond matching the ticket's AC, the old setting MISREPORTED prices: it
 * rounds, so a 19,99 € catalogue product rendered « 20 € ». The catalogue
 * fallback this ticket adds makes catalogue prices — which are overwhelmingly
 * not round — the common case rather than the rare one. Screenshots from
 * OKS-986/990/994 showing « 89 € » will now show « 89,00 € »; that is expected.
 *
 * ⚠️ `Intl` emits U+202F (narrow no-break space) before `€` in `fr-FR`. Any
 * assertion or Playwright locator must build its expectation with the same
 * `Intl` call rather than typing a space — a test that fails only on the space
 * is a false red, and chasing it costs more than the assertion is worth.
 *
 * @returns the formatted amount, or `null` when either half is missing — the
 *   caller then drops the whole clause rather than rendering a dangling
 *   separator (`Qté 1 ·` with nothing after it is a defect, not a degraded
 *   render).
 */
export function formatGiftValue(
  cents: number | null | undefined,
  currency: string | null | undefined,
  locale: Locale,
): string | null {
  // ⚠️ `== null` catches null AND undefined while admitting `0`, which is a
  // legal `mission` amount (D1). `!cents` would suppress a value the brand
  // explicitly entered.
  if (cents == null || !currency) return null

  try {
    return new Intl.NumberFormat(locale === "fr" ? "fr-FR" : "en-US", {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(cents / 100)
  } catch {
    // A currency code `Intl` rejects (a tampered or legacy row) must not throw
    // the surrounding card away.
    //
    // ⚠️ `toFixed(2)`, not the shipped `Math.round`. The three copies this
    // replaces rounded here — dropping the cents in the one branch that exists
    // to preserve the amount when the currency cannot be rendered.
    return `${(cents / 100).toFixed(2)}`
  }
}
